import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/data/status-badge";
import { useAplicarNumerosDaConferencia, useConferenciaDaVenda } from "@/hooks/use-mercado";
import { usePode } from "@/lib/permissoes";
import { useToast } from "@/hooks/use-toast";
import { ErroApi } from "@/lib/queryClient";
import { fmtKgBR, fmtMoedaBR } from "@/lib/formato-mercado";
import { dataCurtaBR } from "@shared/boletim-periodo";
import { diaCivilNaFazenda } from "@shared/data-fazenda";
import { divergenciaDaConferencia, rotuloDivergencia } from "@shared/mercado";
import type { LinhaConferidaDTO } from "@shared/mercado-conferencia";
import { rotuloItemDeVenda } from "@shared/mercado-produtos";

/**
 * "Conferência de {comprador}" — o que ele contou na balança, ao lado do que foi
 * emitido.
 *
 * Vale o que o comprador conferiu (Hugo, 10/09: kg; 24/09: R$/kg também) — é a
 * base do que o produtor cobra. A plataforma não arbitra: quem resolve são os
 * dois. Quando o produtor dá razão ao comprador, "Aplicar os números de
 * {comprador}" (25/09) leva kg e R$/kg dele para a venda SEM invalidar a
 * conferência — corrigir à mão invalidava, e o comprador tinha de conferir de
 * novo o que já batia. Os números vêm prontos do servidor
 * (`montarConferenciaDTO`, o MESMO DTO que o comprador e o link público leem);
 * aqui nada se soma.
 *
 * `focar` = a tela foi aberta pelo chip "Divergência" (`?secao=conferencia`): a
 * seção rola para a vista.
 */
export function ConferenciaDaVenda({
  compartilhamentoId, compradorNome, focar, vendaId, vendaNumero, vendaStatus,
}: {
  compartilhamentoId: string;
  compradorNome: string;
  focar: boolean;
  vendaId: string;
  /** "056/2026" — a frase da confirmação. */
  vendaNumero: string;
  vendaStatus: string;
}) {
  const { data, isLoading, isError } = useConferenciaDaVenda(compartilhamentoId);
  const ref = useRef<HTMLElement>(null);
  const podeOperar = usePode("cacho.vendas.operar");
  const aplicar = useAplicarNumerosDaConferencia();
  const { toast } = useToast();
  const [confirmar, setConfirmar] = useState(false);

  useEffect(() => {
    if (focar && data && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focar, data]);

  if (isLoading) return null;
  if (isError) {
    return (
      <section className="rounded-xl border p-4 text-sm text-destructive" role="alert" data-testid="conferencia-erro">
        Não consegui carregar a conferência de {compradorNome}. Recarregue a página.
      </section>
    );
  }
  const c = data?.conferencia;
  if (!c) return null;

  const divergencia = divergenciaDaConferencia(c);
  const quem = data?.compradorNome ?? compradorNome;
  const quando = dataCurtaBR(diaCivilNaFazenda(c.atualizadoEm ?? c.registradoEm));

  return (
    <section id="conferencia" ref={ref} className="rounded-xl border bg-card scroll-mt-20" data-testid="secao-conferencia">
      <header className="px-4 py-2.5 border-b flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-semibold text-sm">Conferência de {quem}</h3>
        {c.invalidadaEm ? (
          <StatusBadge tom="neutro" ponto>Anterior à correção</StatusBadge>
        ) : divergencia ? (
          <StatusBadge tom="erro" ponto testId="badge-divergencia">{rotuloDivergencia(divergencia)}</StatusBadge>
        ) : (
          <StatusBadge tom="sucesso" ponto>Bate com o romaneio</StatusBadge>
        )}
      </header>

      {/* Sem `table-fixed`: quatro colunas curtas cabem em 390px sem rolagem lateral. */}
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-muted/40 text-muted-foreground uppercase text-[10px] font-semibold tracking-wider">
            <th className="text-left px-3 py-2">Produto</th>
            <th className="text-right px-3 py-2">Emitido</th>
            <th className="text-right px-3 py-2">Conferido</th>
            <th className="text-right px-3 py-2">Diferença</th>
          </tr>
        </thead>
        <tbody>
          {c.linhas.map(l => (
            <tr key={`${l.variedade}|${l.qualidade ?? ""}`} className="border-t">
              <td className="px-3 py-2 font-medium">{rotuloItemDeVenda(l)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtKgBR(l.kgRomaneio)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtKgBR(l.kgConferido)}</td>
              <td className={`px-3 py-2 text-right font-mono tabular-nums ${l.deltaKg !== 0 ? "text-erro font-semibold" : "text-muted-foreground"}`}>
                {l.deltaKg === 0 ? "—" : `${l.deltaKg > 0 ? "+" : "−"}${fmtKgBR(Math.abs(l.deltaKg))}`}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t font-semibold">
            <td className="px-3 py-2">Total</td>
            <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtKgBR(c.totalKgRomaneio)}</td>
            <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtKgBR(c.totalKgConferido)}</td>
            <td className={`px-3 py-2 text-right font-mono tabular-nums ${c.deltaKg !== 0 ? "text-erro" : "text-muted-foreground"}`}>
              {c.deltaKg === 0 ? "—" : `${c.deltaKg > 0 ? "+" : "−"}${fmtKgBR(Math.abs(c.deltaKg))}`}
            </td>
          </tr>
        </tfoot>
      </table>

      {/* O PREÇO, só quando ele diverge (24/09): uma segunda tabela curta em vez
          de três colunas a mais na primeira — sete colunas não cabem em 390px. */}
      {c.precoDivergente && (
        <table className="w-full text-xs border-t" data-testid="tabela-preco-conferido">
          <thead>
            <tr className="bg-muted/40 text-muted-foreground uppercase text-[10px] font-semibold tracking-wider">
              <th className="text-left px-3 py-2">R$/kg</th>
              <th className="text-right px-3 py-2">Emitido</th>
              <th className="text-right px-3 py-2">Conferido</th>
              <th className="text-right px-3 py-2">Diferença</th>
            </tr>
          </thead>
          <tbody>
            {c.linhas.map(l => (
              <tr key={`preco-${l.variedade}|${l.qualidade ?? ""}`} className="border-t">
                <td className="px-3 py-2 font-medium">{rotuloItemDeVenda(l)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoedaBR(l.precoKg)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoedaBR(l.precoKgConferido)}</td>
                <td className={`px-3 py-2 text-right font-mono tabular-nums ${l.deltaPrecoKg !== 0 ? "text-erro font-semibold" : "text-muted-foreground"}`}>
                  {l.deltaPrecoKg === 0 ? "—" : `${l.deltaPrecoKg > 0 ? "+" : "−"}${fmtMoedaBR(Math.abs(l.deltaPrecoKg))}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="px-4 py-3 border-t space-y-1 text-xs text-muted-foreground">
        <p>
          {quem} conferiu em {quando}: {fmtMoedaBR(c.valorConferido)} pelos números dele, contra {fmtMoedaBR(c.valorRomaneio)} emitidos.
        </p>
        {c.observacao && <p className="text-foreground">“{c.observacao}”</p>}
        {c.invalidadaEm
          ? <p>Você corrigiu a venda depois desta conferência — vale o romaneio de novo, até {quem} conferir outra vez.</p>
          : divergencia && (
            <p className="text-foreground" data-testid="texto-vale-conferido">
              Vale o que o comprador conferiu: <b className="tabular-nums">{fmtMoedaBR(c.valorConferido)}</b>.
              Se ele tiver razão, aplique os números dele; se não, converse com ele.
            </p>
          )}
      </div>

      {/* O atalho de quem já decidiu que o comprador tem razão. Secundário de
          propósito: a ação da tela continua sendo cobrar/receber. Venda paga
          fica com o botão desabilitado E o motivo dito — botão cinza sem
          explicação é o que faz a pessoa achar que o app travou. */}
      {!c.invalidadaEm && divergencia && podeOperar && (
        <div className="px-4 pb-3 flex flex-col gap-1.5 md:flex-row md:items-center">
          <Button
            variant="outline"
            className="h-11 w-full md:w-auto"
            disabled={vendaStatus !== "EMITIDA" || aplicar.isPending}
            onClick={() => setConfirmar(true)}
            data-testid="button-aplicar-numeros"
          >
            <Check className="h-4 w-4 mr-1.5" />Aplicar os números de {quem}
          </Button>
          {vendaStatus === "PAGA" && (
            <p className="text-[11px] text-muted-foreground" data-testid="motivo-aplicar-desabilitado">
              A venda já está paga: o dinheiro fechou sobre o romaneio emitido.
            </p>
          )}
        </div>
      )}

      {confirmar && (
        <Dialog open onOpenChange={aberto => { if (!aberto) setConfirmar(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Aplicar os números de {quem}?</DialogTitle>
              <DialogDescription data-testid="texto-confirmar-aplicar">
                A venda {vendaNumero} passa a {fmtMoedaBR(c.valorConferido)}
                {mudancasDoAceite(c.linhas) ? ` (${mudancasDoAceite(c.linhas)})` : ""}. O romaneio muda para os dois lados.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirmar(false)}>Cancelar</Button>
              <Button
                disabled={aplicar.isPending}
                data-testid="button-confirmar-aplicar"
                onClick={async () => {
                  try {
                    const r = await aplicar.mutateAsync(vendaId);
                    toast({ title: `Números de ${quem} aplicados.`, description: `A venda ${vendaNumero} passou a ${fmtMoedaBR(r.valorDepois)}.` });
                    setConfirmar(false);
                  } catch (e) {
                    const mensagem = e instanceof ErroApi ? e.mensagem : (e as Error).message;
                    toast({ title: "Não deu para aplicar", description: mensagem, variant: "destructive" });
                  }
                }}
              >
                {aplicar.isPending ? "Aplicando…" : "Aplicar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}

/**
 * "Prata 2ª a R$ 0,60" · "Prata 1ª com 5.200 kg" — o que muda, linha a linha,
 * dos números que o servidor já montou. Nada é somado aqui.
 */
function mudancasDoAceite(linhas: LinhaConferidaDTO[]): string {
  return linhas
    .filter(l => l.deltaKg !== 0 || l.deltaPrecoKg !== 0)
    .map(l => {
      const partes: string[] = [];
      if (l.deltaKg !== 0) partes.push(`com ${fmtKgBR(l.kgConferido)}`);
      if (l.deltaPrecoKg !== 0) partes.push(`a ${fmtMoedaBR(l.precoKgConferido)}`);
      return `${rotuloItemDeVenda(l)} ${partes.join(" ")}`;
    })
    .join(", ");
}
